import React from 'react';

/**
 * Privacy Policy page component
 * 
 * Provides detailed information about how user data is collected, used, and protected
 * in compliance with global privacy regulations
 */
const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-gradient-to-b from-[#f8f8ff] to-[#f4f4fa] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Hero section with gradient text */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
            Privacy Policy
          </h1>
          <p className="text-gray-600 text-lg font-light">
            Last Updated: May 6, 2025
          </p>
        </div>
        
        {/* Decorative element */}
        <div className="relative mb-12">
          <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-blue-100 opacity-50 blur-xl"></div>
          <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-purple-100 opacity-50 blur-xl"></div>
          <div className="relative z-10 h-0.5 w-full bg-gradient-to-r from-blue-200 via-indigo-300 to-purple-200"></div>
        </div>
        
        <div className="prose prose-lg max-w-none">
          {/* Introduction section */}
          <div className="mb-12">
            <h2 id="introduction" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</span>
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              At Raidu, we respect your privacy and are committed to protecting your personal data. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website or use our services.
            </p>
          </div>
          
          {/* Information We Collect section */}
          <div className="mb-12">
            <h2 id="information-we-collect" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</span>
              Information We Collect
            </h2>
            <p>We collect several types of information, including:</p>
            <ul>
              <li>
                <strong>Personal Information:</strong> Name, email address, phone number, and company 
                details when you contact us, sign up for our services, or request a demo.
              </li>
              <li>
                <strong>Usage Data:</strong> Information about how you interact with our website, 
                including pages visited, time spent on pages, and other browsing actions.
              </li>
              <li>
                <strong>Device Information:</strong> IP address, browser type, operating system, 
                and other technical information about the device you use to access our website.
              </li>
              <li>
                <strong>Cookies and Similar Technologies:</strong> We use cookies and similar tracking 
                technologies to collect information about your browsing activities. See our Cookie Policy 
                section for more details.
              </li>
            </ul>
          </div>
          
          {/* How We Use Your Information section */}
          <div className="mb-12">
            <h2 id="how-we-use-your-information" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">3</span>
              How We Use Your Information
            </h2>
            <p>We use the information we collect for various purposes, including:</p>
            <ul>
              <li>Providing, operating, and maintaining our website and services</li>
              <li>Improving, personalizing, and expanding our website and services</li>
              <li>Understanding and analyzing how you use our website</li>
              <li>Developing new products, services, features, and functionality</li>
              <li>Communicating with you about our services, updates, and other information</li>
              <li>Marketing and advertising purposes</li>
              <li>Preventing fraud and enhancing security</li>
            </ul>
          </div>
          
          {/* Cookie Policy section */}
          <div className="mb-12">
            <h2 id="cookie-policy" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">4</span>
              Cookie Policy
            </h2>
            <p>
              Our website uses cookies and similar tracking technologies to enhance your browsing 
              experience, analyze site traffic, and personalize content. We categorize cookies as follows:
            </p>
            <ul>
              <li>
                <strong>Necessary Cookies:</strong> These cookies are essential for the website to 
                function properly and cannot be disabled in our systems. They are usually set in 
                response to actions made by you, such as setting your privacy preferences, logging in, 
                or filling in forms.
              </li>
              <li>
                <strong>Functional Cookies:</strong> These cookies enable the website to provide enhanced 
                functionality and personalization. They may be set by us or by third-party providers 
                whose services we have added to our pages.
              </li>
              <li>
                <strong>Analytics Cookies:</strong> These cookies allow us to count visits and traffic 
                sources so we can measure and improve the performance of our site. They help us to know 
                which pages are the most and least popular and see how visitors move around the site.
              </li>
              <li>
                <strong>Marketing Cookies:</strong> These cookies may be set through our site by our 
                advertising partners. They may be used by those companies to build a profile of your 
                interests and show you relevant advertisements on other sites.
              </li>
            </ul>
            <p>
              You can manage your cookie preferences at any time by clicking on the "Cookie Preferences" 
              button in the footer of our website.
            </p>
          </div>
          
          {/* Data Sharing and Disclosure section */}
          <div className="mb-12">
            <h2 id="data-sharing" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">5</span>
              Data Sharing and Disclosure
            </h2>
            <p>We may share your information with:</p>
            <ul>
              <li>
                <strong>Service Providers:</strong> Third-party vendors who provide services on our behalf, 
                such as website hosting, data analysis, payment processing, and customer service.
              </li>
              <li>
                <strong>Business Partners:</strong> Companies with whom we partner to offer joint promotional 
                offers or services.
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to protect our rights, 
                privacy, safety, or property.
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of 
                all or a portion of our assets.
              </li>
            </ul>
          </div>
          
          {/* Data Security section */}
          <div className="mb-12">
            <h2 id="data-security" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">6</span>
              Data Security
            </h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. However, 
              no method of transmission over the Internet or electronic storage is 100% secure, and we 
              cannot guarantee absolute security.
            </p>
          </div>
          
          {/* International Data Transfers section */}
          <div className="mb-12">
            <h2 id="international-transfers" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">7</span>
              International Data Transfers
            </h2>
            <p>
              Your information may be transferred to, and processed in, countries other than the country 
              in which you reside. These countries may have data protection laws that differ from your 
              country. We take steps to ensure that your information receives an adequate level of 
              protection in the countries in which we process it.
            </p>
          </div>
          
          {/* Your Rights section */}
          <div className="mb-12">
            <h2 id="your-rights" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">8</span>
              Your Rights
            </h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li>Access to your personal information</li>
              <li>Correction of inaccurate or incomplete information</li>
              <li>Deletion of your personal information</li>
              <li>Restriction of processing of your personal information</li>
              <li>Data portability</li>
              <li>Objection to processing of your personal information</li>
              <li>Withdrawal of consent</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided in the 
              "Contact Us" section below.
            </p>
          </div>
          
          {/* Children's Privacy section */}
          <div className="mb-12">
            <h2 id="childrens-privacy" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">9</span>
              Children's Privacy
            </h2>
            <p>
              Our website and services are not directed to individuals under the age of 16. We do not 
              knowingly collect personal information from children. If you believe we have collected 
              personal information from a child, please contact us so we can take appropriate steps to 
              delete such information.
            </p>
          </div>
          
          {/* Changes to This Privacy Policy section */}
          <div className="mb-12">
            <h2 id="changes" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">10</span>
              Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or 
              for other operational, legal, or regulatory reasons. We will notify you of any material 
              changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>
          
          {/* Contact Us section */}
          <div className="mb-12">
            <h2 id="contact-us" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">11</span>
              Contact Us
            </h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, 
              please contact us at:
            </p>
            <p>
              Raidu Inc.<br />
              271 Little Falls Dr.<br />
              Wilmington, Delaware 19808<br />
              United States<br />
              Email: privacy@raidu.com
            </p>
          </div>
        </div>
        

      </div>
    </div>
  );
};

export default PrivacyPolicy;
